import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiClient } from '../api/apiClient';
import { PaymentMethodsResponse } from '../../entities/PaymentMethods';

// Configurar el cliente API con la misma configuración que useInvoicePortal
const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/';
const apiClient = new ApiClient({
  baseURL: baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL,
});

interface UsePaymentMethodsResult {
  data: PaymentMethodsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePaymentMethods = (
  shortUuid: string | undefined
): UsePaymentMethodsResult => {
  const [data, setData] = useState<PaymentMethodsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const currentUuid = useRef<string | undefined>(undefined);

  const fetchPaymentMethods = useCallback(async () => {
    if (!shortUuid) {
      setError('UUID es requerido');
      setIsLoading(false);
      return;
    }

    // Evitar llamadas duplicadas para el mismo UUID
    if (hasFetched.current && currentUuid.current === shortUuid) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      hasFetched.current = true;
      currentUuid.current = shortUuid;

      const response = await apiClient.getPaymentMethods(shortUuid);
      setData(response.data);
    } catch (err: any) {
      console.error('Error fetching payment methods:', err);

      if (err.status === 404) {
        setError('Métodos de pago no encontrados');
      } else if (err.status === 410) {
        setError('Enlace de pago ya no está disponible');
      } else {
        setError(err.message || 'Error al cargar los métodos de pago');
      }
    } finally {
      setIsLoading(false);
    }
  }, [shortUuid]);

  useEffect(() => {
    // Reset cuando cambia el UUID
    if (currentUuid.current !== shortUuid) {
      hasFetched.current = false;
      setData(null);
      setError(null);
      currentUuid.current = shortUuid;
    }

    if (shortUuid && !hasFetched.current) {
      fetchPaymentMethods();
    }
  }, [shortUuid, fetchPaymentMethods]);

  const refetch = useCallback(() => {
    hasFetched.current = false; // Permite refetch manual
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
